import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { poweredByUrl } from "../components/PoweredBy";
import {
  Calendar,
  Clock,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  MapPin,
  Phone,
  User,
  Info,
  MessageCircle,
  CalendarClock,
} from "lucide-react";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { es } from "date-fns/locale";
import AppDownloadCta from "../components/AppDownloadCta";
import {
  DateStep,
  TimeStep,
  type Slot,
} from "../components/AppointmentDateTimeSteps";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:4000/api";

// Solicitud de cambio pendiente de revisión por el centro. El backend la
// guarda en `Appointment.pendingChangeRequest` y la expone tanto en el GET
// de la cita como en la respuesta del POST `/change`. Shape defensivo (mismo
// criterio que `PendingChangeRequest` de `ReservationManage.tsx`): si algún
// día viniera sin `changes` ya formateado, se compone un resumen a partir de
// `date`/`startTime` sueltos.
interface PendingAppointmentChangeRequest {
  date?: string;
  startTime?: string;
  specialistId?: string | null;
  requestedAt?: string;
  changes?: string[];
}

interface AppointmentData {
  confirmationCode: string;
  date: string;
  startTime: string;
  endTime: string;
  status: string;
  // Añadidos para poder pedir un cambio de día/hora sin salir de esta
  // pantalla: slug de la sede (para pedir calendario/horas reales) y datos
  // del servicio/profesional actuales (para no perder el profesional
  // asignado al pedir el cambio).
  slug: string;
  serviceId: string;
  specialistId: string | null;
  durationMinutes: number;
  pendingChangeRequest?: PendingAppointmentChangeRequest | null;
  pendingChangeRequestedAt?: string | null;
  // Política de cancelación del centro (21-oct-2026). Fuera de plazo no se
  // enseña el botón de cancelar: antes salía siempre y el rechazo llegaba al
  // confirmar, y el cliente creía que le dejaba. Sin el campo (servidor
  // antiguo) se comporta como antes.
  cancellation?: {
    allowed: boolean;
    minHours: number;
    canCancelNow: boolean;
  };
  service: {
    name: string;
    durationMinutes: number;
    price: number | null;
  };
  specialist: {
    firstName: string;
    lastName: string | null;
  };
  customer: {
    firstName: string;
    phone: string | null;
  };
  business: {
    name: string;
    address: string | null;
    phone: string | null;
    logo?: string | null;
    rescheduleMethod?: "phone" | "whatsapp";
    rescheduleWhatsappNumber?: string | null;
  };
}

/** «24 horas», «1 hora», «1,5 horas». */
function formatHours(hours: number): string {
  const value = Number.isInteger(hours) ? String(hours) : String(hours).replace(".", ",");
  return `${value} ${hours === 1 ? "hora" : "horas"}`;
}

function capitalizeFirst(text: string): string {
  return text.length > 0 ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

// Resume en un texto legible la solicitud de cambio pendiente, igual que
// `formatPendingChangeSummary` en `ReservationManage.tsx` pero sin `useT()`
// (esta pantalla sigue fija en español, fuera de alcance de la i18n del
// widget de mesas — ver `.claude/memorias/frontend.notas.md`, 19-ago-2026).
function formatPendingAppointmentChangeSummary(
  pending: PendingAppointmentChangeRequest,
): string {
  if (pending.changes && pending.changes.length > 0) {
    return pending.changes.join(", ");
  }
  const parts: string[] = [];
  if (pending.date) {
    parts.push(
      `al ${capitalizeFirst(
        format(new Date(pending.date), "EEEE d 'de' MMMM", { locale: es }),
      )}`,
    );
  }
  if (pending.startTime) {
    parts.push(`a las ${pending.startTime}`);
  }
  return parts.join(" ");
}

/** Deja solo dígitos y, si el resultado tiene 9 (número español sin
 * prefijo), antepone "34". Heurística simple pensada para España, que es
 * el mercado actual del producto. */
function cleanWhatsappNumber(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  return digits.length === 9 ? `34${digits}` : digits;
}

const statusConfig: Record<
  string,
  { label: string; color: string; bgColor: string }
> = {
  pending: {
    label: "Pendiente",
    color: "text-yellow-700",
    bgColor: "bg-yellow-100",
  },
  confirmed: {
    label: "Confirmada",
    color: "text-blue-700",
    bgColor: "bg-blue-100",
  },
  double_confirmed: {
    label: "Confirmada por ti",
    color: "text-emerald-700",
    bgColor: "bg-emerald-100",
  },
  completed: {
    label: "Completada",
    color: "text-emerald-700",
    bgColor: "bg-emerald-100",
  },
  cancelled: {
    label: "Cancelada",
    color: "text-red-700",
    bgColor: "bg-red-100",
  },
  no_show: {
    label: "No asistió",
    color: "text-gray-700",
    bgColor: "bg-gray-100",
  },
};

export default function AppointmentManage() {
  const { code } = useParams<{ code: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [appointment, setAppointment] = useState<AppointmentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelled, setCancelled] = useState(false);

  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  // Pedir otro día/hora: mismo par de pasos (calendario + rejilla de horas)
  // que el flujo de cita nueva, ver `components/AppointmentDateTimeSteps.tsx`.
  const [showChangeFlow, setShowChangeFlow] = useState(false);
  const [changeStep, setChangeStep] = useState<"date" | "time">("date");
  const [changeDate, setChangeDate] = useState("");
  const [changeTime, setChangeTime] = useState("");
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());
  const [calendarDays, setCalendarDays] = useState<
    { date: string; available: boolean }[]
  >([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [changeSubmitting, setChangeSubmitting] = useState(false);
  const [changeError, setChangeError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!code) return;
      try {
        setLoading(true);
        const res = await fetch(
          `${API_URL}/public/appointments/code/${encodeURIComponent(code)}`,
        );
        const json = await res.json();
        if (!res.ok || !json.success) {
          throw new Error(json.message || "Cita no encontrada");
        }
        setAppointment(json.data);
      } catch (e: any) {
        setError(e.message || "No se pudo cargar la cita");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [code]);

  const handleCancel = async () => {
    if (!code) return;
    try {
      setCancelling(true);
      setCancelError(null);
      const res = await fetch(
        `${API_URL}/public/appointments/code/${encodeURIComponent(code)}/cancel`,
        { method: "POST" },
      );
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.message || "No se pudo cancelar la cita");
      }
      setCancelled(true);
      setShowCancelConfirm(false);
      if (appointment) {
        setAppointment({ ...appointment, status: "cancelled" });
      }
    } catch (e: any) {
      setCancelError(e.message || "No se pudo cancelar la cita");
    } finally {
      setCancelling(false);
    }
  };

  const handleConfirm = async () => {
    if (!code) return;
    try {
      setConfirming(true);
      setConfirmError(null);
      const res = await fetch(
        `${API_URL}/public/appointments/code/${encodeURIComponent(code)}/confirm`,
        { method: "POST" },
      );
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.message || "No se pudo confirmar la cita");
      }
      setConfirmed(true);
      if (appointment) {
        setAppointment({
          ...appointment,
          status: json.data?.status ?? "double_confirmed",
        });
      }
    } catch (e: any) {
      setConfirmError(e.message || "No se pudo confirmar la cita");
    } finally {
      setConfirming(false);
    }
  };

  // Solo se muestran horas donde sigue disponible el profesional ya asignado
  // a la cita (si lo hay) — mismo criterio de `visibleSlots` en
  // `AppointmentsApp.tsx`: no se ofrece cambiar de profesional aquí, solo de
  // día/hora, así que las horas donde solo hay hueco con OTRO profesional no
  // sirven para esta solicitud.
  const visibleSlots = useMemo(() => {
    if (!appointment?.specialistId) return slots;
    return slots.filter((s) =>
      s.specialists.some((sp) => sp.id === appointment.specialistId),
    );
  }, [slots, appointment?.specialistId]);

  const loadSlotsForDate = useCallback(
    (dateStr: string) => {
      if (!appointment) return;
      setSlotsLoading(true);
      const url = new URL(
        `${API_URL}/public/appointments/${appointment.slug}/slots`,
      );
      url.searchParams.set("date", dateStr);
      url.searchParams.set("serviceId", appointment.serviceId);
      // Su propia cita no debe contar como ocupada: sin esto, el cliente se
      // bloquea a sí mismo y no puede conservar la hora cambiando solo de
      // profesional (ni ver libre su hueco si al final no cambia nada).
      url.searchParams.set("excludeCode", appointment.confirmationCode);
      fetch(url.toString())
        .then((r) => r.json())
        .then((res) => {
          if (res.success) setSlots(res.data);
        })
        .finally(() => setSlotsLoading(false));
    },
    [appointment],
  );

  // Calendario del mes al entrar en el paso "date" del flujo de cambio (o al
  // navegar de mes). Mismo endpoint que la cita nueva.
  useEffect(() => {
    if (!showChangeFlow || changeStep !== "date" || !appointment) return;
    setCalendarLoading(true);
    const from = format(startOfMonth(calendarMonth), "yyyy-MM-dd");
    const to = format(endOfMonth(calendarMonth), "yyyy-MM-dd");
    const url = new URL(
      `${API_URL}/public/appointments/${appointment.slug}/calendar`,
    );
    url.searchParams.set("from", from);
    url.searchParams.set("to", to);
    url.searchParams.set("serviceId", appointment.serviceId);
    if (appointment.specialistId)
      url.searchParams.set("specialistId", appointment.specialistId);
    fetch(url.toString())
      .then((r) => r.json())
      .then((res) => {
        if (res.success) setCalendarDays(res.data);
      })
      .finally(() => setCalendarLoading(false));
  }, [
    showChangeFlow,
    changeStep,
    calendarMonth,
    appointment?.slug,
    appointment?.serviceId,
    appointment?.specialistId,
  ]);

  // Horas del día elegido en el flujo de cambio.
  useEffect(() => {
    if (!showChangeFlow || !changeDate) {
      setSlots([]);
      return;
    }
    loadSlotsForDate(changeDate);
  }, [showChangeFlow, changeDate, loadSlotsForDate]);

  function openChangeFlow() {
    setChangeError(null);
    setChangeDate("");
    setChangeTime("");
    setChangeStep("date");
    setCalendarMonth(new Date());
    setShowChangeFlow(true);
  }

  async function handleRequestChange(newDate: string, newTime: string) {
    if (!code) return;
    setChangeSubmitting(true);
    setChangeError(null);
    try {
      const res = await fetch(
        `${API_URL}/public/appointments/code/${encodeURIComponent(code)}/change`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            date: newDate,
            startTime: newTime,
            ...(appointment?.specialistId
              ? { specialistId: appointment.specialistId }
              : {}),
          }),
        },
      );
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(
          json.message || "No se pudo pedir el cambio. Inténtalo de nuevo.",
        );
      }
      // La cita ORIGINAL sigue siendo la que manda hasta que el centro
      // apruebe: solo actualizamos `pendingChangeRequest`, nunca la
      // fecha/hora de la propia cita (aunque el backend mande de vuelta un
      // `appointment` con más campos, se fusiona sin pisar lo que ya
      // teníamos por si esa respuesta viene incompleta).
      setAppointment((prev) =>
        prev
          ? {
              ...prev,
              ...(json.appointment ?? {}),
              pendingChangeRequest:
                json.pendingChange ?? json.appointment?.pendingChangeRequest ?? {
                  date: newDate,
                  startTime: newTime,
                },
              pendingChangeRequestedAt:
                json.appointment?.pendingChangeRequestedAt ??
                new Date().toISOString(),
            }
          : prev,
      );
      setShowChangeFlow(false);
      setChangeStep("date");
      setChangeDate("");
      setChangeTime("");
    } catch (e: unknown) {
      setChangeError(
        e instanceof Error
          ? e.message
          : "No se pudo pedir el cambio. Inténtalo de nuevo.",
      );
      // El hueco puede haberse ocupado justo ahora (409): refrescamos las
      // horas de ese mismo día para que desaparezca, sin sacar al cliente
      // del paso en el que está.
      setChangeTime("");
      loadSlotsForDate(newDate);
    } finally {
      setChangeSubmitting(false);
    }
  }

  function handleSelectChangeSlot(slot: Slot) {
    setChangeTime(slot.time);
    handleRequestChange(changeDate, slot.time);
  }

  // Auto-trigger desde el query param ?action=confirm|cancel (links del email)
  useEffect(() => {
    if (!appointment) return;
    const action = searchParams.get("action");
    if (!action) return;
    setSearchParams({}, { replace: true });
    if (
      action === "confirm" &&
      !confirmed &&
      !confirming &&
      !["cancelled", "completed", "no_show", "double_confirmed"].includes(
        appointment.status,
      )
    ) {
      handleConfirm();
    }
    if (
      action === "cancel" &&
      !cancelled &&
      !cancelling &&
      !["cancelled", "completed", "no_show"].includes(appointment.status)
    ) {
      setShowCancelConfirm(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointment]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  if (error || !appointment) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            Cita no encontrada
          </h1>
          <p className="text-gray-600">{error || "El código no es válido"}</p>
        </div>
      </div>
    );
  }

  const status = statusConfig[appointment.status] || statusConfig.pending;
  const isFinal = ["cancelled", "completed", "no_show"].includes(
    appointment.status,
  );
  const hasPendingChangeRequest = !!appointment.pendingChangeRequest;
  const pendingChangeSummary = appointment.pendingChangeRequest
    ? formatPendingAppointmentChangeSummary(appointment.pendingChangeRequest)
    : "";
  const currentSpecialistName =
    `${appointment.specialist.firstName} ${appointment.specialist.lastName ?? ""}`.trim() ||
    null;

  const dateObj = new Date(appointment.date);
  const dateLabel = format(dateObj, "EEEE d 'de' MMMM 'de' yyyy", {
    locale: es,
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-8 text-white">
            {appointment.business.logo && (
              <img
                src={appointment.business.logo}
                alt={appointment.business.name}
                className="w-20 h-20 rounded-full mx-auto mb-3 object-cover bg-white"
              />
            )}
            <div className="flex items-center justify-between mb-2">
              <h1 className="text-2xl font-bold">
                {appointment.business.name}
              </h1>
              <span
                className={`${status.bgColor} ${status.color} px-3 py-1 rounded-full text-xs font-semibold`}
              >
                {status.label}
              </span>
            </div>
            <p className="text-indigo-100 text-sm">
              Código:{" "}
              <span className="font-mono font-semibold">
                {appointment.confirmationCode}
              </span>
            </p>
          </div>

          {/* Mensaje de cancelación reciente */}
          {cancelled && (
            <div className="bg-emerald-50 border-l-4 border-emerald-500 px-6 py-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-emerald-900">
                    Cita cancelada correctamente
                  </p>
                  <p className="text-sm text-emerald-700 mt-1">
                    Te hemos enviado la confirmación. Si quieres reagendar,
                    contacta directamente con el establecimiento.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Mensaje de confirmación reciente */}
          {confirmed && (
            <div className="bg-emerald-50 border-l-4 border-emerald-500 px-6 py-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-emerald-900">
                    ¡Cita confirmada!
                  </p>
                  <p className="text-sm text-emerald-700 mt-1">
                    Hemos avisado al negocio de que asistirás. Si te surge
                    cualquier imprevisto, puedes cancelar desde aquí.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Detalles */}
          <div className="px-6 py-6 space-y-4">
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  Fecha
                </p>
                <p className="text-gray-900 font-medium capitalize">
                  {dateLabel}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  Hora
                </p>
                <p className="text-gray-900 font-medium">
                  {appointment.startTime} – {appointment.endTime}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  Servicio
                </p>
                <p className="text-gray-900 font-medium">
                  {appointment.service.name}
                  <span className="text-gray-500 font-normal ml-2">
                    ({appointment.service.durationMinutes} min
                    {appointment.service.price != null
                      ? ` · ${appointment.service.price.toFixed(2)} €`
                      : ""}
                    )
                  </span>
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <User className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  Profesional
                </p>
                <p className="text-gray-900 font-medium">
                  {appointment.specialist.firstName}{" "}
                  {appointment.specialist.lastName ?? ""}
                </p>
              </div>
            </div>

            {appointment.business.address && (
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    Dirección
                  </p>
                  <p className="text-gray-900 font-medium">
                    {appointment.business.address}
                  </p>
                </div>
              </div>
            )}

            {appointment.business.phone && (
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">
                    Teléfono
                  </p>
                  <a
                    href={`tel:${appointment.business.phone}`}
                    className="text-indigo-600 font-medium hover:underline"
                  >
                    {appointment.business.phone}
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Acciones */}
          {!isFinal && !cancelled && (
            <div className="px-6 pb-6 border-t border-gray-100 pt-6">
              {showChangeFlow ? (
                <div className="space-y-4">
                  {changeStep === "date" && (
                    <DateStep
                      month={calendarMonth}
                      onMonthChange={setCalendarMonth}
                      calendarDays={calendarDays}
                      calendarLoading={calendarLoading}
                      selectedDate={changeDate}
                      onDateSelect={(d) => {
                        setChangeDate(d);
                        setChangeTime("");
                        setChangeError(null);
                        setChangeStep("time");
                      }}
                      onBack={() => {
                        setShowChangeFlow(false);
                        setChangeError(null);
                      }}
                      serviceName={appointment.service.name}
                      durationMinutes={appointment.durationMinutes}
                      specialistName={currentSpecialistName}
                    />
                  )}

                  {changeStep === "time" &&
                    (changeSubmitting ? (
                      <div className="flex flex-col items-center justify-center py-10 gap-3 text-gray-500">
                        <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                        <p className="text-sm">Enviando tu solicitud…</p>
                      </div>
                    ) : (
                      <>
                        {changeError && (
                          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                            <span>{changeError}</span>
                          </div>
                        )}
                        <TimeStep
                          date={changeDate}
                          slots={visibleSlots}
                          slotsLoading={slotsLoading}
                          selectedTime={changeTime}
                          onSelectSlot={handleSelectChangeSlot}
                          onBack={() => {
                            setChangeStep("date");
                            setChangeError(null);
                          }}
                          onChangeDay={() => {
                            setChangeStep("date");
                            setChangeError(null);
                          }}
                          serviceName={appointment.service.name}
                          durationMinutes={appointment.durationMinutes}
                          specialistName={currentSpecialistName}
                        />
                      </>
                    ))}
                </div>
              ) : !showCancelConfirm ? (
                <div className="space-y-3">
                  {appointment.status !== "double_confirmed" && !confirmed && (
                    <button
                      onClick={handleConfirm}
                      disabled={confirming}
                      className="w-full bg-emerald-600 text-white hover:bg-emerald-700 font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {confirming ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <CheckCircle className="w-5 h-5" />
                      )}
                      Confirmo mi asistencia
                    </button>
                  )}
                  {confirmError && (
                    <p className="text-sm text-red-600">{confirmError}</p>
                  )}

                  {hasPendingChangeRequest && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center text-sm text-blue-800">
                      <strong>Cambio pendiente de confirmar</strong>
                      <br />
                      Has pedido cambiarla{" "}
                      {pendingChangeSummary || "a otro día u hora"}. El centro
                      tiene que confirmarlo.
                    </div>
                  )}

                  <button
                    onClick={openChangeFlow}
                    disabled={confirming}
                    className="w-full bg-white hover:bg-indigo-50 text-indigo-700 font-semibold py-3 px-4 rounded-lg border-2 border-indigo-200 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <CalendarClock className="w-5 h-5" />
                    Pedir otro día u hora
                  </button>

                  {appointment.cancellation?.canCancelNow === false ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900 flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>
                        {appointment.cancellation.allowed
                          ? `Ya no puedes cancelar la cita online: el centro pide al menos ${formatHours(
                              appointment.cancellation.minHours,
                            )} de antelación.`
                          : "Este centro no permite cancelar citas online."}{" "}
                        Si necesitas cancelarla, contacta con el centro
                        {appointment.business.phone ? (
                          <>
                            {" "}
                            en el{" "}
                            <a
                              href={`tel:${appointment.business.phone}`}
                              className="font-semibold underline"
                            >
                              {appointment.business.phone}
                            </a>
                          </>
                        ) : null}
                        .
                      </span>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowCancelConfirm(true)}
                      className="w-full bg-red-50 text-red-700 hover:bg-red-100 font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <XCircle className="w-5 h-5" />
                      Cancelar cita
                    </button>
                  )}
                  {appointment.business.rescheduleMethod === "whatsapp" &&
                  appointment.business.rescheduleWhatsappNumber ? (
                    <a
                      href={`https://wa.me/${cleanWhatsappNumber(
                        appointment.business.rescheduleWhatsappNumber,
                      )}?text=${encodeURIComponent(
                        `Hola, quiero reagendar mi cita ${appointment.confirmationCode} del ${dateLabel} a las ${appointment.startTime}.`,
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full bg-green-600 text-white hover:bg-green-700 font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <MessageCircle className="w-5 h-5" />
                      Escribir para reagendar
                    </a>
                  ) : (
                    appointment.business.phone && (
                      <a
                        href={`tel:${appointment.business.phone}`}
                        className="w-full bg-indigo-600 text-white hover:bg-indigo-700 font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <Phone className="w-5 h-5" />
                        Llamar para reagendar
                      </a>
                    )
                  )}
                </div>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-900 font-medium mb-3">
                    ¿Seguro que quieres cancelar tu cita? Esta acción no se
                    puede deshacer.
                  </p>
                  {cancelError && (
                    <p className="text-sm text-red-700 mb-3">{cancelError}</p>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowCancelConfirm(false)}
                      disabled={cancelling}
                      className="flex-1 bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 font-semibold py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
                    >
                      Volver
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={cancelling}
                      className="flex-1 bg-red-600 text-white hover:bg-red-700 font-semibold py-2 px-4 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {cancelling ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <XCircle className="w-4 h-4" />
                      )}
                      Sí, cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <AppDownloadCta />

        <p className="text-center text-xs text-gray-500 mt-6">
          Con la tecnología de{" "}
          <a
            href={poweredByUrl(null, "gestion-cita")}
            className="text-indigo-600 hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            ReservaManager
          </a>
        </p>
      </div>
    </div>
  );
}
